import React, {
  useEffect,
  useState,
  Fragment,
  useCallback,
  useReducer, forwardRef, useImperativeHandle,
} from 'react';
import Box from '@mui/material/Box';
import {
  Alert,
  Button,
  Button as MUIButton,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { MainTableSkeleton } from '../Loading/MainTableSkeleton';
import { Entity } from '../../types/entities/Entity';
import { Schema } from '../../types/Schema';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import api from '../../services/api';
import { useStateContext } from '../../hooks/useStateContext';
import { debounce } from 'lodash';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ExportAirportCard } from '../Custom/Action/ExportAirportCard';
import { Roadmap } from '../Custom/Action/Roadmap';
import { PrintRoadmap } from '../Custom/Action/PrintRoadmap';
import { GuideSchedule } from '../Custom/Action/GuideSchedule';
import { FeedbackFormComponent } from '../Custom/Action/FeedbackForm';
import WarningIcon from '../../assets/icons/warning-96.png';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";
import PendingRowUpdateDialog from "../Table/layout/PendingRowUpdateDialog";
import PendingRowUpdateStatusDialog from "../Table/layout/PendingRowUpdateStatusDialog";

const reducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_INPUT':

      const newCurrent = {
        ...state.current,
        [action.payload.column]: action.payload.value,
      };
      const newHistory = {
        ...state.history,
        [action.payload.column]: action.payload.value,
      };

      if (action?.schema && (action?.schema?.name === 'Services' || action?.schema?.name === 'Activity') && action.payload.column === 'name') {
        newCurrent['sp_name'] = action.payload.value;
        newHistory['sp_name'] = action.payload.value;
      }

      return {
        ...state,
        history: newHistory,
        current: newCurrent,
      };
    case 'SET_DEFAULT':
      return {
        ...state,
        current: action.payload,
      };
    case 'SET_HISTORY':
      return {
        ...state,
        history: action.payload,
      };
    default:
      return state;
  }
};

interface CompleteContentProps {
  schema: any;
  propertiesFromTable: any;
  hasSlidingPane?: boolean;
  handleSubmit: (item: any) => void;
  onClose: (item: any) => void;
  forcedRefresh?: () => void;
  onChangeFullWidth: (value: boolean) => void;
  id: number | undefined | string;
  isLocked?: any;
  isInFullPanel?: boolean;
}

const CompleteContent = forwardRef((props: CompleteContentProps, ref:any) => {

  const {
    schema,
    propertiesFromTable,
    handleSubmit,
    onChangeFullWidth,
    hasSlidingPane,
    onClose,
    forcedRefresh,
    id,
    isLocked,
    isInFullPanel,
  } = props;

  useImperativeHandle(ref, () => ({
    handleSubmit: (value: any) => {
      onSubmit();
    },
  }));

  const [loadingSave, setLoadingSave] = useState(false);
  const [showSendNotificationButton, setShowSendNotificationButton] = useState(
    false
  );
  const [showSendNotification, setShowSendNotification] = useState(false);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState<any>(propertiesFromTable);
  const [modified, setModified] = React.useState(false);
  const [state, dispatch] = useReducer(reducer, {
    history: {},
    current: undefined,
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const { user } = useStateContext();
  const [requiredFieldsFromDatabase, setRequiredFieldsFromDatabase] = useState<
    any
  >([]);
  const [missingFields, setMissingFields] = useState<
    Array<keyof typeof schema.columns>
  >([]);
  const [confirmBeforeUpdateRowDialog, setConfirmBeforeUpdateRowDialog] = useState<any>(null);
  const [confirmBeforeUpdateRowDialogOpen, setConfirmBeforeUpdateRowDialogOpen] = useState<boolean>(false);
  const [confirmBeforeUpdateRowDialogType, setConfirmBeforeUpdateRowDialogType] = useState<any>(null);

  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  const fetchSchema = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({});

    try {
      schema.client.readOne(propertiesFromTable?.id).then((res: any) => {
        dispatch({
          type: 'SET_DEFAULT',
          payload: res,
        });
        setLoading(false);
      });
    } catch (e) {
      setLoading(false);
    }
  }, [schema, propertiesFromTable]);

  const getMissingFields = useCallback(async () => {
    const requiredFieldFromView = await api
      .get(`/required-fields/${user.id}/${schema?.viewTable}`)
      .then((res: any) => {
        setRequiredFieldsFromDatabase(res);
        return res;
      });

    const columnKeys: any = schema?.columns ? Object.keys(schema.columns) : [];

    const requiredFields = columnKeys.filter((columnKey: string) => {
      const column: any = schema.columns[columnKey as keyof {}];

      const required = requiredFieldFromView[`${columnKey}`];

      if (schema?.viewTable) {
        return required;
      }

      return column.required && !column.readOnly;
    });

    return requiredFields.filter(
      (requiredField: any) =>
        state.current[requiredField] === undefined ||
        state.current[requiredField] === null ||
        state.current[requiredField] === ''
    );
  }, [state]);

  const onSubmit = useCallback(
    (close?: boolean) => {
      if (schemaHasClient(schema)) {
        setLoadingSave(true);
        schema.client
          .update(propertiesFromTable.id as number, {
            ...state.history,
            ['schemaColumns']: schema?.columns,
          })
          .then((res: any) => {
            dispatch({
              type: 'SET_HISTORY',
              payload: {},
            });
            setModified(false);
            handleSubmit(res);
            if (close) {
              onClose(false);
            }
            toast.success(`Successfully saved ${schema.name}`);

            const failedFieldsArray:any = res?.unsaved_fields ? {...res.unsaved_fields} : {};

            if (Object.keys(failedFieldsArray).length){
              fetchSchema();
            }

            Object.keys(failedFieldsArray).map((field:any) => {
              failedFieldsArray[field].map((message:any)=>{
                toast.error(message);
              });
            });
          })
          .catch((error) => {
            toast.success(`Error: Unable to save ${schema.name}`);
          })
          .finally(() => {
            setLoadingSave(false);
            setOpenConfirmDialog(false);
          });
      }
      return true;
    },
    [getMissingFields, state, schema]
  );

  function handleSetChanges(column: any, value: any) {

    if (column === 'start_time') {
      setConfirmBeforeUpdateRowDialog(properties);
    }

    if (schema?.viewTable === 'service_reservations' && column === 'service_reservation_status' && value?.id === 6) {
      setConfirmBeforeUpdateRowDialog(properties);
      setConfirmBeforeUpdateRowDialogType('status');
    }

    dispatch({ type: 'SET_INPUT', payload: { column, value }, schema: schema });
  }

  useEffect(() => {
    fetchSchema();
  }, []);

  const debouncedChange = useCallback(
    debounce(() => {
      if (!modified && Object.keys(state.history).length) {
        setModified(true);
      }
    }, 300),
    [modified, state]
  );

  useEffect(() => {
    debouncedChange();
  }, [state, debouncedChange]);

  const theme = createTheme({
    typography: {
      subtitle1: {
        fontSize: '14px',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              fontSize: '14px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
            },
          },
        },
      },
    },
  });

  return (
    <>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        sx={{
          background: '#256861',
          color: '#fff',
          px: '10px',
          opacity: loadingSave ? 0.6 : 1,
          pointerEvents: loadingSave ? 'none' : 'auto',
          filter: loadingSave ? 'grayscale(1)' : 'none',
          userSelect: 'none',
          boxShadow: '0px 0px 0px 0px #000',
        }}
      >
        <Stack direction={'row'} alignItems={'center'}>
          <IconButton
            aria-label="Close full view"
            sx={{
              color: '#fff',
            }}
            onClick={() => {
              onChangeFullWidth(false);
            }}
          >
            <CloseFullscreenRoundedIcon />
          </IconButton>
          <Typography variant="subtitle1">
            <>
              <span>Edit {schema?.name}</span>
              {properties && properties?.name && (
                <span> // {properties?.name}</span>
              )}
            </>
          </Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'center'}>
          {showSendNotificationButton && (
            <IconButton
              aria-label="Sent"
              sx={{
                color: '#fff',
              }}
              onClick={() => {
                setShowSendNotification(true);
              }}
            >
              <EmailOutlinedIcon />
            </IconButton>
          )}

          <IconButton
            aria-label="Close"
            color="primary"
            onClick={() => {
              if (modified) {
                setOpenConfirmDialog(true);
              } else {
                onClose(properties);
              }
            }}
          >
            <CloseIcon
              sx={{
                color: '#fff',
              }}
            />
          </IconButton>
        </Stack>
      </Stack>

      {loading && !state.current ? (
        <MainTableSkeleton />
      ) : (
        <>
          {schema?.sections ? (
            <>
              <Grid
                container
                spacing={isInFullPanel? 1: 2}
                p={'30px'}
                sx={{
                  '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0, 0, 0, 0.26)',
                    borderRadius: '4px',
                  },
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 40px)',
                  marginTop: '0px',
                  userSelect: 'none',
                  opacity: loadingSave ? 0.6 : 1,
                  pointerEvents: loadingSave ? 'none' : 'auto',
                  filter: loadingSave ? 'grayscale(1)' : 'none',
                  background: isInFullPanel ?'#fbfbfb' :'#f1f1f1',
                  '& .MuiFormControl-root.MuiTextField-root, .MuiInputBase-root.MuiOutlinedInput-root, .DraftEditor-root': {
                    background: '#fff',
                  },
                  '& .DraftEditor-root': {
                    padding: '0px 10px',
                  },
                  '& .rdw-editor-wrapper': {
                    borderRadius: '4px',
                  },
                }}
              >
                {schema.sections.map((section: any, rowIndex: number) => {
                  return (
                    <>
                      <ThemeProvider theme={theme}>
                        <Grid
                          container
                          xs={section.width}
                          item
                          spacing={2}
                          key={rowIndex}
                          alignContent={'flex-start'}
                          sx={{
                            opacity: (isLocked) ? 0.6 : 1,
                            pointerEvents: (isLocked) ? 'none' : 'auto',
                          }}
                        >
                          {section?.label && (
                            <Stack width={'100%'}>
                              <Stack
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                              >
                                <Stack
                                  direction={'row'}
                                  gap={2}
                                  alignItems={'center'}
                                  sx={{
                                    marginTop: '20px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    gutterBottom
                                    fontWeight={'bold'}
                                    sx={{
                                      marginBottom: '0px',
                                    }}
                                  >
                                    {section?.label}
                                  </Typography>
                                  {section?.actions && (
                                    <>
                                      {section?.actions.map((action: any) => {
                                        if (action === 'group-roadmap-print') {
                                          return <PrintRoadmap />;
                                        }
                                        if (action === 'group-roadmap-button') {
                                          return <Roadmap />;
                                        }
                                        if (
                                          action ===
                                          'group-guide-schedule-button'
                                        ) {
                                          return (
                                            <GuideSchedule
                                              values={state.current}
                                              listItem={false}
                                            />
                                          );
                                        }
                                        if (
                                          action ===
                                          'group-feedback-form-button'
                                        ) {
                                          return <FeedbackFormComponent />;
                                        }
                                        if (
                                          action === 'group-airport-card-button'
                                        ) {
                                          return (
                                            <ExportAirportCard
                                              group={state.current}
                                            />
                                          );
                                        }

                                        return <></>;
                                      })}
                                    </>
                                  )}
                                </Stack>

                                <Stack direction={'row'}>
                                  {section?.hasDefaultButtons && (
                                    <>
                                      <Button
                                        variant={'text'}
                                        onClick={() => {
                                          if (modified) {
                                            setOpenConfirmDialog(true);
                                          } else {
                                            onClose(properties);
                                          }
                                        }}
                                      >
                                        Close
                                      </Button>
                                      {modified && (
                                        <LoadingButton
                                          loading={loadingSave}
                                          variant="contained"
                                          onClick={() => {
                                            if (confirmBeforeUpdateRowDialog){
                                              setConfirmBeforeUpdateRowDialogOpen(true);
                                            }else{
                                              onSubmit();
                                            }
                                          }}
                                        >{`Save`}</LoadingButton>
                                      )}
                                    </>
                                  )}
                                </Stack>
                              </Stack>
                              <Divider />
                            </Stack>
                          )}
                          <Grid container item spacing={2}>
                            {section.grids.map((grid: any, gridKey: number) => (
                              <Grid item xs={grid.width} key={gridKey}>
                                <Stack direction={'column'}>
                                  {grid.columns.map(
                                    (column: any, columnKey: number) => {
                                      if (column?.grid) {
                                        return (
                                          <Stack direction={'row'} gap={2}>
                                            {column.columns.map(
                                              (
                                                column: any,
                                                columnKey: number
                                              ) => {
                                                const {
                                                  fieldRenderer: Field,
                                                  required,
                                                  readOnly,
                                                  columnSchema,
                                                  columnName,
                                                  editOnlyByAdmin,
                                                  accordingTo,
                                                } = column;
                                                const isMissing = missingFields.includes(
                                                  column
                                                );

                                                let readOnlyTemp = (!isAdmin && editOnlyByAdmin) ? true : readOnly;

                                                const key:any = accordingTo;

                                                let accordingToSendData: any = undefined;

                                                if (
                                                  state.current[key] !== undefined &&
                                                  state.current[key] !== null &&
                                                  state.current[key] !== ''
                                                ) {
                                                  const accordingToData: any = state.current[key];
                                                  accordingToSendData = {
                                                    name: key,
                                                    id: accordingToData?.id,
                                                    value: accordingToData,
                                                  };
                                                }

                                               /* if (column?.columnName === 'service_reservation_status'){
                                                  readOnlyTemp = true;
                                                  if (isAdmin){
                                                    readOnlyTemp = false;
                                                  }
                                                  if (state.current['service_provider'] && state.current['service_provider']['dont_send_confirmation']){
                                                    readOnlyTemp = false;
                                                  }

                                                }*/
                                                if (
                                                  schema?.viewTable === 'service_reservations' &&
                                                  column?.columnName === 'staff'
                                                ) {
                                                  readOnlyTemp = true;
                                                  if (
                                                    state.current?.is_guide
                                                    && !state.current?.selected_staff?.id
                                                  ){
                                                    readOnlyTemp = false;
                                                  }

                                                }

                                                return (
                                                  <Stack
                                                    key={columnKey}
                                                    direction={'column'}
                                                    gap={1}
                                                    alignContent={'center'}
                                                    sx={{
                                                      width: '100%',
                                                      marginY: '2px',
                                                    }}
                                                  >
                                                    {column?.name && (
                                                      <Box
                                                        sx={{
                                                          flexShrink:
                                                            section?.name ===
                                                            'duration_timing'
                                                              ? 3
                                                              : 0,
                                                          width:
                                                            section?.name ===
                                                            'duration_timing'
                                                              ? '100%'
                                                              : '150px',
                                                        }}
                                                      >
                                                        <Typography
                                                          variant="subtitle1"
                                                          fontWeight={'bold'}
                                                          sx={{
                                                            marginTop: '9px',
                                                          }}
                                                        >
                                                          <span>
                                                            {column?.name}
                                                          </span>
                                                          {column?.info && (
                                                            <Tooltip
                                                              arrow={true}
                                                              title={
                                                                column?.info
                                                              }
                                                              slotProps={{
                                                                popper: {
                                                                  sx: {
                                                                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                                                                      marginTop:
                                                                        '5px',
                                                                    },
                                                                    [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
                                                                      marginBottom:
                                                                        '5px',
                                                                    },
                                                                    [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
                                                                      marginLeft:
                                                                        '5px',
                                                                    },
                                                                    [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                                                                      marginRight:
                                                                        '5px',
                                                                    },
                                                                  },
                                                                },
                                                              }}
                                                            >
                                                              <IconButton
                                                                size={'small'}
                                                              >
                                                                <InfoOutlinedIcon
                                                                  sx={{
                                                                    fontSize:
                                                                      '15px',
                                                                  }}
                                                                />
                                                              </IconButton>
                                                            </Tooltip>
                                                          )}
                                                        </Typography>
                                                      </Box>
                                                    )}
                                                    <Stack
                                                      sx={{
                                                        width:
                                                          section?.name ===
                                                          'duration_timing'
                                                            ? '250px'
                                                            : '100%',
                                                      }}
                                                      alignItems={'flex-start'}
                                                    >
                                                      <Field
                                                        onChange={(
                                                          value: any,
                                                          column: string
                                                        ) => {
                                                          handleSetChanges(
                                                            column
                                                              ? column
                                                              : columnName,
                                                            value
                                                          );
                                                          return true;
                                                        }}
                                                        values={state.current}
                                                        defaultValue={
                                                          state.current[
                                                            columnName
                                                          ]
                                                        }
                                                        disabled={readOnlyTemp}
                                                        allValue={state.current}
                                                        column={column}
                                                        fullWidth={true}
                                                        schema={schema}
                                                        columnSchema={
                                                          columnSchema
                                                        }
                                                        forcedRefresh={() => {
                                                          if (forcedRefresh) {
                                                            forcedRefresh();
                                                          }
                                                        }}
                                                        accordingToValue={accordingToSendData}
                                                      />
                                                      {isMissing && (
                                                        <Alert
                                                          severity="error"
                                                          sx={{
                                                            py: '0px',
                                                          }}
                                                        >
                                                          {' '}
                                                          This field is missing
                                                        </Alert>
                                                      )}
                                                    </Stack>
                                                  </Stack>
                                                );
                                              }
                                            )}
                                          </Stack>
                                        );
                                      }

                                      const {
                                        fieldRenderer: Field,
                                        required,
                                        readOnly,
                                        columnSchema,
                                        columnName,
                                        accordingTo,
                                        editOnlyByAdmin,
                                        type
                                      } = column;
                                      const isMissing = missingFields.includes(
                                        column
                                      );

                                      let readOnlyTemp = (!isAdmin && editOnlyByAdmin) ? true : readOnly;

                                      const key:any = accordingTo;

                                      let accordingToSendData: any = undefined;

                                      if (
                                        state.current[key] !== undefined &&
                                        state.current[key] !== null &&
                                        state.current[key] !== ''
                                      ) {
                                        const accordingToData: any = state.current[key];
                                        accordingToSendData = {
                                          name: key,
                                          id: accordingToData?.id,
                                          value: accordingToData,
                                        };
                                      }

                                      /*if (column?.columnName === 'service_reservation_status'){
                                        readOnlyTemp = true;
                                        if (isAdmin){
                                          readOnlyTemp = false;
                                        }
                                        if (state.current['service_provider'] && state.current['service_provider']['dont_send_confirmation']){
                                          readOnlyTemp = false;
                                        }
                                      }*/

                                      if (
                                        schema?.viewTable === 'service_reservations' &&
                                        column?.columnName === 'staff'
                                      ) {
                                        readOnlyTemp = true;
                                        if (
                                          state.current?.is_guide
                                          && !state.current?.selected_staff?.id
                                        ){
                                          readOnlyTemp = false;
                                        }

                                      }

                                      return (
                                        <Stack
                                          key={columnKey}
                                          direction={type==='boolean'?'row':'column'}
                                          gap={1}
                                          alignContent={'center'}
                                          sx={{
                                            width: '100%',
                                            marginY: '2px',
                                          }}
                                        >
                                          {column?.name && (
                                            <Box
                                              sx={{
                                                flexShrink:
                                                  section?.name ===
                                                  'duration_timing'
                                                    ? 3
                                                    : 0,
                                                width:
                                                  section?.name ===
                                                  'duration_timing'
                                                    ? '100%'
                                                    : '170px',
                                              }}
                                            >
                                              <Typography
                                                variant="subtitle1"
                                                fontWeight={'bold'}
                                                sx={{
                                                  marginTop: '9px',
                                                }}
                                              >
                                                <span>{column?.name}</span>
                                                {column?.info && (
                                                  <Tooltip
                                                    arrow={true}
                                                    title={column?.info}
                                                    slotProps={{
                                                      popper: {
                                                        sx: {
                                                          [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
                                                            marginTop: '5px',
                                                          },
                                                          [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
                                                            marginBottom: '5px',
                                                          },
                                                          [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
                                                            marginLeft: '5px',
                                                          },
                                                          [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                                                            marginRight: '5px',
                                                          },
                                                        },
                                                      },
                                                    }}
                                                  >
                                                    <IconButton size={'small'}>
                                                      <InfoOutlinedIcon
                                                        sx={{
                                                          fontSize: '15px',
                                                        }}
                                                      />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                              </Typography>
                                            </Box>
                                          )}
                                          <Stack
                                            sx={{
                                              width:
                                                section?.name ===
                                                'duration_timing'
                                                  ? '250px'
                                                  : '100%',
                                            }}
                                            alignItems={'flex-start'}
                                          >
                                            <Field
                                              onChange={(
                                                value: any,
                                                column: string
                                              ) => {
                                                handleSetChanges(
                                                  column ? column : columnName,
                                                  value
                                                );
                                                return true;
                                              }}
                                              values={state.current}
                                              defaultValue={
                                                state.current[columnName]
                                              }
                                              disabled={readOnlyTemp}
                                              allValue={state.current}
                                              column={column}
                                              fullWidth={true}
                                              schema={schema}
                                              columnSchema={columnSchema}
                                              forcedRefresh={() => {
                                                if (forcedRefresh) {
                                                  forcedRefresh();
                                                }
                                              }}
                                              accordingToValue={accordingToSendData}
                                            />
                                            {isMissing && (
                                              <Alert
                                                severity="error"
                                                sx={{
                                                  py: '0px',
                                                }}
                                              >
                                                {' '}
                                                This field is missing
                                              </Alert>
                                            )}
                                          </Stack>
                                        </Stack>
                                      );
                                    }
                                  )}
                                </Stack>
                              </Grid>
                            ))}
                          </Grid>
                        </Grid>
                      </ThemeProvider>
                    </>
                  );
                })}
              </Grid>
            </>
          ) : (
            <>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
              >
                <Stack alignItems={'center'}>
                  <Box>
                    <img src={WarningIcon} alt="" />
                  </Box>
                  <Typography color={'#7e7e7e'}>
                    There is no content to display
                  </Typography>
                  <Typography variant={'caption'} color={'#7e7e7e'}>
                    click{' '}
                    <Link
                      onClick={() => {
                        onChangeFullWidth(false);
                      }}
                      variant="body2"
                    >
                      here
                    </Link>{' '}
                    to open in Side Pane
                  </Typography>
                </Stack>
              </Box>
            </>
          )}
        </>
      )}

      {modified && (
        <>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{
              background: '#fff',
              px: '20px',
              py: '10px',
              opacity: loadingSave ? 0.6 : 1,
              pointerEvents: loadingSave ? 'none' : 'auto',
              borderTop: '1px solid #e0e0e0',
              boxShadow: '0px -4px 10px 0px #00000017',
            }}
          >
            <Stack direction={'row'} alignItems={'center'}></Stack>
            <Stack direction={'row'} alignItems={'center'}>
              <Button
                variant="contained"
                onClick={() => {
                  if (confirmBeforeUpdateRowDialog){
                    setConfirmBeforeUpdateRowDialogOpen(true);
                  }else{
                    onSubmit();
                  }
                }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {confirmBeforeUpdateRowDialogOpen && (
        <>
          {confirmBeforeUpdateRowDialogType === 'status' ? (
            <PendingRowUpdateStatusDialog
              open={confirmBeforeUpdateRowDialogOpen}
              data={confirmBeforeUpdateRowDialog}
              onClose={()=>{
                setConfirmBeforeUpdateRowDialogOpen(false);
              }}
              onConfirm={()=>{
                setConfirmBeforeUpdateRowDialogOpen(false);
                setConfirmBeforeUpdateRowDialog(null);
                setConfirmBeforeUpdateRowDialogType('');
                onSubmit();
              }}
            />
          ) :(
            <PendingRowUpdateDialog
              open={confirmBeforeUpdateRowDialogOpen}
              data={confirmBeforeUpdateRowDialog}
              onClose={()=>{
                setConfirmBeforeUpdateRowDialogOpen(false);
              }}
              onConfirm={()=>{
                setConfirmBeforeUpdateRowDialogOpen(false);
                setConfirmBeforeUpdateRowDialog(null);
                setConfirmBeforeUpdateRowDialogType('');
                onSubmit();
              }}
            />
          )}
        </>

      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {
              'You have unsaved changes. Please save your work before leaving. Do you want to close without saving?'
            }
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'outlined'}
              color={'error'}
              onClick={() => {
                onClose(false);
              }}
            >
              Close Without Saving
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'primary'}
              onClick={() => {
                onSubmit(true);
              }}
              autoFocus
            >
              Save and Close
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
});

export default CompleteContent;

function schemaHasClient<T extends Entity>(
  schema: Schema<T>
): schema is SchemaWithClient<T> {
  return 'client' in schema;
}
